import React from 'react';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../translations';
import { 
    Home, Save, Trash2, Upload, X, 
    Package, Store, ShoppingBag, Briefcase, Factory, Coffee, 
    Utensils, Wrench, Hammer, Globe, Truck, Anchor, Zap, Crown, Camera,
    Image as ImageIcon, AlertCircle
} from 'lucide-react';

const Card = ({ children, className = "", noPadding = false }) => (
  <div className={`bg-white rounded-3xl border border-slate-100 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.08)] transition-all duration-300 ${className}`}>
    {noPadding ? children : <div className="p-6 md:p-8">{children}</div>}
  </div>
);

const Input = ({ label, className, ...props }) => (
  <div className="flex flex-col gap-2 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <div className="relative">
      <input 
        className={`
          w-full px-4 py-3 rounded-xl border border-slate-200 bg-white
          focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none
          transition-all duration-200 text-slate-700 placeholder:text-slate-400
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          ${className || ''}
        `}
        {...props}
      />
    </div>
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div className="flex flex-col gap-2 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <div className="relative">
      <select 
        className={`
          w-full px-4 py-3 rounded-xl border border-slate-200 bg-white
          focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none
          transition-all duration-200 text-slate-700
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          appearance-none
        `}
        {...props}
      >
        {children}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  </div>
);

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false, icon: Icon, fullWidth = false, ...props }) => {
  const variants = {
    primary: "bg-[#10056E] hover:bg-[#0d0459] text-white shadow-lg shadow-[#10056E]/30",
    secondary: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm",
    danger: "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30",
    ghost: "bg-transparent hover:bg-slate-100 text-slate-600",
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`
        inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation
        disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100
        ${variants[variant]} 
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {Icon && <Icon size={18} className={disabled ? 'animate-spin' : ''} />}
      {children}
    </button>
  );
};

/**
 * Project Details Component
 * Handles company details, warehouse info, and project identity settings
 */
const ProjectDetails = React.memo(({
    activeProject,
    selectedProjectIcon,
    setSelectedProjectIcon,
    setConfirmModal,
    db,
    fetchData,
    showToast,
    requestConfirm,
    can,
    setIsDeleteProjectModalOpen,
    setDeleteProjectPassword,
    setDeleteProjectError
}) => {
    const { language } = useLanguage();
    const t = getTranslation(language);
    return (
        <div className="space-y-6 max-w-4xl">
            {/* Company Details */}
            <Card>
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-1">{t("settings.companyDetails")}</h3>
                    <p className="text-sm text-slate-500">{t("settings.displayedOnInvoicePO")}</p>
                </div>
                <form id="projectDetailsForm" className="space-y-6">
                    <Input 
                        label={t("settings.registeredCompanyName")} 
                        name="companyName"
                        defaultValue={activeProject.companyName} 
                        placeholder="E.g.: Bangi Auto Parts Sdn Bhd"
                    />
                    
                    {/* Company Logo Upload */}
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                            {t("settings.companyLogoLabel")}
                        </label>
                        <p className="text-xs text-slate-500 mb-3">{t("settings.logoDisplayedOnPDF")}</p>
                        <div className="flex items-start gap-4">
                            {/* Logo Preview */}
                            <div className="flex-shrink-0">
                                {activeProject.companyLogo ? (
                                    <div className="relative w-32 h-32 border-2 border-slate-200 rounded-xl overflow-hidden bg-white">
                                        <img 
                                            src={activeProject.companyLogo} 
                                            alt="Company Logo" 
                                            className="w-full h-full object-contain p-2"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setConfirmModal({
                                                    isOpen: true,
                                                    title: t('settings.deleteCompanyLogo'),
                                                    message: t('settings.confirmDeleteLogo'),
                                                    onConfirm: async () => {
                                                        try {
                                                            await db.updateProject(activeProject.id, { companyLogo: '' });
                                                            fetchData();
                                                            showToast(t('settings.logoDeleted'));
                                                        } catch (err) {
                                                            showToast(t('settings.failedDeleteLogo'), 'error');
                                                        }
                                                        setConfirmModal({ isOpen: false });
                                                    }
                                                });
                                            }}
                                            className="absolute top-1 right-1 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600 transition-colors"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center bg-slate-50">
                                        <ImageIcon size={32} className="text-slate-400" />
                                    </div>
                                )}
                            </div>
                            
                            {/* Upload Button */}
                            <div className="flex-1">
                                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 cursor-pointer transition-all">
                                    <Upload size={16} />
                                    {activeProject.companyLogo ? t('settings.changeLogo') : t('settings.uploadLogo')}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                if (file.size > 2 * 1024 * 1024) {
                                                    showToast(t('settings.imageTooLarge'), 'error');
                                                    return;
                                                }
                                                const reader = new FileReader();
                                                reader.onloadend = async () => {
                                                    try {
                                                        await db.updateProject(activeProject.id, { companyLogo: reader.result });
                                                        fetchData();
                                                        showToast(t('settings.logoUpdated'));
                                                    } catch (err) {
                                                        showToast(t('settings.failedUploadLogo'), 'error');
                                                    }
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                    />
                                </label>
                                <p className="text-xs text-slate-500 mt-2">{t("settings.formatMaxSize")}</p>
                                <p className="text-xs text-slate-500">{t("settings.recommendedSize")}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label={t("settings.companySSMNo")} 
                            name="companySsm"
                            defaultValue={activeProject.companySsm} 
                            placeholder="202301045123"
                        />
                        <Input 
                            label={t("settings.officialPhone")} 
                            name="companyPhone"
                            defaultValue={activeProject.companyPhone} 
                            placeholder="03-xxxxxxxx"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label={t("settings.officialEmail")} 
                            name="companyEmail"
                            type="email"
                            defaultValue={activeProject.companyEmail} 
                            placeholder="info@company.com"
                        />
                        <Input 
                            label={t("settings.websiteLabel")} 
                            name="companyWebsite"
                            defaultValue={activeProject.companyWebsite} 
                            placeholder="www.company.com"
                        />
                    </div>
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">{t("settings.companyMailingAddress")}</label>
                        <Input 
                            name="companyAddressLine1"
                            defaultValue={activeProject.companyAddressLine1} 
                            placeholder="E.g.: No. 12, Jalan Industri 3, Taman Perindustrian Bangi"
                        />
                        <Input 
                            name="companyAddressLine2"
                            defaultValue={activeProject.companyAddressLine2} 
                            placeholder="E.g.: 43650 Bandar Baru Bangi, Selangor"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                name="companyPostcode"
                                defaultValue={activeProject.companyPostcode} 
                                placeholder={t("settings.postcodeLabel")}
                            />
                            <Select 
                                name="companyState"
                                defaultValue={activeProject.companyState || 'W.P. Kuala Lumpur'}
                            >
                                <option value="Johor">Johor</option>
                                <option value="Kedah">Kedah</option>
                                <option value="Kelantan">Kelantan</option>
                                <option value="Melaka">Melaka</option>
                                <option value="Negeri Sembilan">Negeri Sembilan</option>
                                <option value="Pahang">Pahang</option>
                                <option value="Penang">Penang</option>
                                <option value="Perak">Perak</option>
                                <option value="Perlis">Perlis</option>
                                <option value="Sabah">Sabah</option>
                                <option value="Sarawak">Sarawak</option>
                                <option value="Selangor">Selangor</option>
                                <option value="Terengganu">Terengganu</option>
                                <option value="W.P. Kuala Lumpur">W.P. Kuala Lumpur</option>
                                <option value="W.P. Labuan">W.P. Labuan</option>
                                <option value="W.P. Putrajaya">W.P. Putrajaya</option>
                            </Select>
                        </div>
                    </div>
                </form>
            </Card>

            {/* Warehouse Information */}
            <Card>
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-1 flex items-center gap-2">
                        <Home size={20} className="text-[#10056E]" />
                        {t("settings.warehouseInfoTitle")}
                    </h3>
                    <p className="text-sm text-slate-500">{t("settings.warehouseNameAddress")}</p>
                </div>
                <div className="space-y-6">
                    <Input 
                        label={t("settings.warehouseNameLabel")} 
                        name="warehouseName"
                        form="projectDetailsForm"
                        defaultValue={activeProject.warehouseName || activeProject.warehouse} 
                        placeholder="E.g.: Parts Store Bangi"
                        required
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label={t("settings.warehousePICName")} 
                            name="warehousePicName"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehousePicName} 
                            placeholder="E.g.: Ahmad Faizal"
                        />
                        <Input 
                            label={t("settings.picPhoneNo")} 
                            name="warehousePicPhone"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehousePicPhone} 
                            placeholder="012-9988776"
                        />
                    </div>
                    <Input 
                        label={t("settings.picEmailLabel")} 
                        name="warehousePicEmail"
                        type="email"
                        form="projectDetailsForm"
                        defaultValue={activeProject.warehousePicEmail} 
                        placeholder="ahmad@partsstore.com.my"
                    />
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">{t("settings.warehouseDeliveryAddress")}</label>
                        <Input 
                            name="warehouseAddressLine1"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehouseAddressLine1} 
                            placeholder="E.g.: No. 8, Jalan Industri 5, Taman Perindustrian Bangi"
                        />
                        <Input 
                            name="warehouseAddressLine2"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehouseAddressLine2} 
                            placeholder="E.g.: 43650 Bandar Baru Bangi, Selangor"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                name="warehousePostcode"
                                form="projectDetailsForm"
                                defaultValue={activeProject.warehousePostcode} 
                                placeholder={t("settings.postcodeLabel")}
                            />
                            <Select 
                                name="warehouseState"
                                form="projectDetailsForm"
                                defaultValue={activeProject.warehouseState || 'W.P. Kuala Lumpur'}
                            >
                                <option value="Johor">Johor</option>
                                <option value="Kedah">Kedah</option>
                                <option value="Kelantan">Kelantan</option>
                                <option value="Melaka">Melaka</option>
                                <option value="Negeri Sembilan">Negeri Sembilan</option>
                                <option value="Pahang">Pahang</option>
                                <option value="Penang">Penang</option>
                                <option value="Perak">Perak</option>
                                <option value="Perlis">Perlis</option>
                                <option value="Sabah">Sabah</option>
                                <option value="Sarawak">Sarawak</option>
                                <option value="Selangor">Selangor</option>
                                <option value="Terengganu">Terengganu</option>
                                <option value="W.P. Kuala Lumpur">W.P. Kuala Lumpur</option>
                                <option value="W.P. Labuan">W.P. Labuan</option>
                                <option value="W.P. Putrajaya">W.P. Putrajaya</option>
                            </Select>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Identiti Projek (Project Identity) */}
            <Card>
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-1">{t("settings.projectIdentity")}</h3>
                    <p className="text-sm text-slate-500">{t("settings.projectNameIcon")}</p>
                </div>
                <div className="space-y-6">
                    <Input 
                        label={t("settings.projectNameLabel")} 
                        name="name"
                        form="projectDetailsForm"
                        defaultValue={activeProject.name} 
                        placeholder="E.g.: Bangi Auto Parts Workshop"
                        required
                    />
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t("settings.descriptionLabel")}</label>
                        <textarea 
                            name="description"
                            form="projectDetailsForm"
                            defaultValue={activeProject.description}
                            placeholder="Spare parts inventory for automotive workshop and retail counter."
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none transition-all resize-none"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t("settings.projectIconLabel")}</label>
                        <p className="text-xs text-slate-500 mb-4">{t("settings.chooseIconDesc")}</p>
                        <input type="hidden" name="icon" form="projectDetailsForm" value={selectedProjectIcon} />
                        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                            {[
                                { name: 'Package', component: Package },
                                { name: 'Store', component: Store },
                                { name: 'ShoppingBag', component: ShoppingBag },
                                { name: 'Briefcase', component: Briefcase },
                                { name: 'Factory', component: Factory },
                                { name: 'Coffee', component: Coffee },
                                { name: 'Utensils', component: Utensils },
                                { name: 'Wrench', component: Wrench },
                                { name: 'Hammer', component: Hammer },
                                { name: 'Globe', component: Globe },
                                { name: 'Truck', component: Truck },
                                { name: 'Home', component: Home },
                                { name: 'Anchor', component: Anchor },
                                { name: 'Zap', component: Zap },
                                { name: 'Crown', component: Crown },
                                { name: 'Camera', component: Camera }
                            ].map((icon) => {
                                const IconComponent = icon.component;
                                const isSelected = selectedProjectIcon === icon.name;
                                return (
                                    <button
                                        key={icon.name}
                                        type="button"
                                        onClick={() => setSelectedProjectIcon(icon.name)}
                                        className={`
                                            w-full aspect-square rounded-xl flex items-center justify-center transition-all
                                            ${isSelected 
                                                ? 'bg-[#10056E] text-white shadow-lg shadow-[#10056E]/30 scale-105' 
                                                : 'bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 border border-slate-200'
                                            }
                                        `}
                                    >
                                        <IconComponent size={24} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Save and Delete Buttons */}
            <div className="flex justify-between items-center pt-4">
                {can.deleteProject && (
                    <Button 
                        variant="danger"
                        onClick={() => {
                            setIsDeleteProjectModalOpen(true);
                            setDeleteProjectPassword('');
                            setDeleteProjectError('');
                        }}
                        icon={Trash2}
                    >
                        {t("settings.deleteProject")}
                    </Button>
                )}
                {!can.deleteProject && <div></div>}
                <Button 
                    onClick={() => {
                        requestConfirm(
                            t("settings.saveChanges"), 
                            t('settings.saveConfirm'), 
                            async () => {
                                const form = document.getElementById('projectDetailsForm');
                                const formData = new FormData(form);
                                const updates = {};
                                for (let [key, value] of formData.entries()) {
                                    updates[key] = value;
                                }
                                try {
                                    await db.updateProject(activeProject.id, updates);
                                    fetchData();
                                    showToast(t("settings.saveSuccess"));
                                } catch (err) {
                                    showToast(t("settings.saveError"), "error");
                                }
                            }
                        );
                    }}
                    icon={Save}
                >
                    {t("settings.saveProjectDetails")}
                </Button>
            </div>
        </div>
    );
});

ProjectDetails.displayName = 'ProjectDetails';

export default ProjectDetails;
